import { useMemo, useState } from "react";
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Textarea,
  DateTimePicker,
  MessageTagSelector,
} from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import {
  useAuth,
  useCachedTags,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useUsersQuery,
} from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { Enduser, EnduserSearchCombobox } from "@tellescope/contacts";
import { formatName } from "@tellescope/utils";

interface AddTicketSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTicketCreate?: () => void;
  queueId?: string;
}

export function AddTicketSheet({ open, onOpenChange, onTicketCreate, queueId }: AddTicketSheetProps) {
  const { t } = useTranslation("tickets");
  const { currentUser } = useAuth();
  const { data: cachedTags } = useCachedTags({ model: "tickets" });
  const [selectedEnduser, setSelectedEnduser] = useState<Enduser>();

  const createTicketMutation = useTellescopeMutation({
    model: "tickets",
    method: "createOne",

    onSuccess: () => {
      onOpenChange(false);
    },
  });

  const form = useForm({
    fields: {
      queueId: {
        type: "select",
        initialValue: queueId,
      },
      title: {
        type: "text",
        initialValue: undefined,
        required: true,
      },
      description: {
        type: "textarea",
        initialValue: undefined,
      },
      dueDate: {
        type: "dateTime",
        initialValue: undefined,
      },
      enduserId: {
        type: "select",
        initialValue: undefined,
      },
      userId: {
        type: "select",
        initialValue: currentUser.id,
      },
      priority: {
        type: "select",
        initialValue: "0",
      },
      stage: {
        type: "select",
        initialValue: "Not Started",
      },
      tags: {
        type: "multiselect",
        initialValue: [],
      },
    },
    validateOn: "blur",
  });

  const availableTags = useMemo(
    () => Array.from(new Set(...cachedTags, ...form.values.tags)),
    [cachedTags, form.values.tags]
  );

  const queuesQuery = useTellescopeInfiniteQuery({
    model: "ticket_queues",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  const { data: users } = useUsersQuery();

  const handleOnOpenChange: typeof onOpenChange = (open) => {
    if (!open) {
      form.resetForm();
    }
    onOpenChange(open);
  };

  const handleCreate = () => {
    createTicketMutation.mutate({
      title: form.values.title,
      htmlDescription: form.values.description,
      queueId: form.values.queueId,
      enduserId: form.values.enduserId,
      dueDateInMS: form.values.dueDate?.getTime(),
      tags: form.values.tags,
      stage: form.values.stage,
      priority: Number(form.values.priority),
      owner: form.values.userId,
    });
    onTicketCreate?.();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const enduserFieldProps = form.getCustomFieldProps("enduserId");
  const tagsFieldProps = form.getCustomFieldProps("tags");

  return (
    <Sheet open={open} onOpenChange={handleOnOpenChange}>
      <SheetContent className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{t("Add New Ticket")}</SheetTitle>
          <SheetDescription>{t("Create a new ticket with the form below.")}</SheetDescription>
        </SheetHeader>
        <div className="grid flex-1 auto-rows-min gap-4 px-4 overflow-y-auto">
          {/* Add to Queue */}
          <div className="grid gap-1.5">
            <Label htmlFor="queue">{t("Add to Queue")}</Label>
            <Select {...form.getCustomFieldProps("queueId")}>
              <Select.Trigger id="queue">
                <Select.Value placeholder="Select a queue" />
              </Select.Trigger>
              <Select.Content>
                {queuesQuery.data?.map((queue) => (
                  <Select.Item key={queue.id} value={queue.id}>
                    {queue.title}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </div>

          {/* Title */}
          <div className="grid gap-1.5">
            <Label htmlFor="title">{t("Title")}</Label>
            <Input id="title" {...form.getFieldProps("title")} placeholder="Enter ticket title" />
          </div>

          {/* Description */}
          <div className="grid gap-1.5">
            <Label htmlFor="description">{t("Description")}</Label>
            <Textarea
              id="description"
              placeholder="Describe the issue or request in detail..."
              {...form.getFieldProps("description")}
              className="min-h-[100px]"
            />
          </div>

          {/* Due Date */}
          <div className="grid gap-1.5">
            <DateTimePicker
              {...form.getCustomFieldProps("dueDate")}
              placeholder="Select date and time"
              label={t("Due Date")}
            />
          </div>

          {/* Patient */}
          <div className="grid gap-1.5">
            <Label htmlFor="patient">{t("Patient")}</Label>
            <EnduserSearchCombobox
              value={selectedEnduser ? [selectedEnduser] : []}
              onChange={([enduser]) => {
                setSelectedEnduser(enduser);
                enduserFieldProps.onValueChange(enduser?.id ?? "");
              }}
            />
          </div>

          {/* Assigned To */}
          <div className="grid gap-1.5">
            <Label htmlFor="assigned-to">{t("Assigned To")}</Label>
            <Select {...form.getFieldProps("userId")}>
              <Select.Trigger id="assigned-to">
                <Select.Value placeholder={t("Select assignee")} />
              </Select.Trigger>
              <Select.Content>
                {users?.map((user) => (
                  <Select.Item key={user.id} value={user.id}>
                    {formatName(user)}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </div>

          {/* Priority */}
          <div className="grid gap-1.5">
            <Label htmlFor="priority">{t("Priority")}</Label>
            <Select {...form.getFieldProps("priority")}>
              <Select.Trigger id="priority">
                <Select.Value placeholder={t("Select priority level")} />
              </Select.Trigger>
              <Select.Content>
                <Select.Item value="0">{t("Not Set")}</Select.Item>
                <Select.Item value="10">{t("High")}</Select.Item>
                <Select.Item value="5">{t("Medium")}</Select.Item>
                <Select.Item value="1">{t("Low")}</Select.Item>
              </Select.Content>
            </Select>
          </div>

          {/* Stage */}
          <div className="grid gap-1.5">
            <Label htmlFor="stage">{t("Stage")}</Label>
            <Select {...form.getFieldProps("stage")}>
              <Select.Trigger id="stage">
                <Select.Value placeholder={t("Select current stage")} />
              </Select.Trigger>
              <Select.Content>
                <Select.Item value="Not Started">{t("Not Started")}</Select.Item>
                <Select.Item value="In Progress">{t("In Progress")}</Select.Item>
                <Select.Item value="Blocked">{t("Blocked")}</Select.Item>
                <Select.Item value="Archived">{t("Archived")}</Select.Item>
              </Select.Content>
            </Select>
          </div>

          {/* Tags */}
          <div className="grid gap-1.5">
            <Label>{t("Tags")}</Label>
            <MessageTagSelector
              availableTags={availableTags}
              selectedTags={tagsFieldProps.value}
              onTagsChange={tagsFieldProps.onValueChange}
              placeholder={t("Add tags to categorize this ticket...")}
            />
          </div>
        </div>
        <SheetFooter>
          <Button onClick={handleCreate} disabled={!form.isValid}>
            {t("Create Ticket")}
          </Button>
          <SheetClose asChild>
            <Button variant="outline" onClick={handleCancel}>
              {t("Cancel")}
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
